import type { AxiosError } from "axios";

/** The machine-readable half of a failed Teams call, so the card can say the one
 *  thing the admin can act on instead of a generic failure.
 *
 *  Three codes, and each has a different recourse: `signin_not_configured` and
 *  `bot_not_configured` are 503s meaning this *environment* is not wired up (contact
 *  support — nothing the workspace can fix), and `no_linked_chat` is a 409 meaning
 *  install the app in Teams first. Anything else stays `null` rather than being
 *  guessed at: an unrecognised failure has to reach the user as one. */
export type TTeamsErrorCode = "signin_not_configured" | "bot_not_configured" | "no_linked_chat";

const KNOWN_CODES: TTeamsErrorCode[] = [
  "signin_not_configured",
  "bot_not_configured",
  "no_linked_chat",
];

/** The `code` a Teams endpoint put in its error body, or `null`.
 *
 *  Falls back to `null` rather than inventing one, the way `toConnectError` does for
 *  the LMS store: an unrecognised failure must reach the user as an unrecognised
 *  failure, not as a confident wrong diagnosis. */
export function teamsErrorCode(error: unknown): TTeamsErrorCode | null {
  const data = (error as AxiosError<{ code?: string }>)?.response?.data;
  return KNOWN_CODES.find((known) => known === data?.code) ?? null;
}

/** Whether a failure was the 409 that means "install the Cognota app in Teams first".
 *
 *  Checks the status as well as the code, because that is the pair the endpoint
 *  promises and a 409 from anywhere else on this resource does not exist. */
export function isNoLinkedChatError(error: unknown): boolean {
  const response = (error as AxiosError<{ code?: string }>)?.response;
  return response?.status === 409 && response?.data?.code === "no_linked_chat";
}
